import { useState, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, ImagePlus, Pencil, Check, X, Loader2, Mountain } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import { DirectUploadProvider } from '@docflow/react-activestorage-provider';
import { apiClient, tokenStorage } from '../../utils/api-client';
import { locationImagesService } from '../../services/location-images';
import { useToast } from '../ui/use-toast';
import LocationImageCarousel from './LocationImageCarousel';
import CoverSlideshow from './CoverSlideshow';
import type { GalleryImage } from '../../types/location-images';

interface LocationHeaderProps {
  locationId: string;
  name: string;
  bio?: string;
  coverImageUrl?: string;
  logoUrl?: string;
  logoSmallUrl?: string;
  galleryImages?: GalleryImage[];
  fallbackIcon?: React.ReactNode;
  isStaffMember: boolean;
  isStaffAdmin: boolean;
  /** Hide the name heading (e.g. when it is already shown in the breadcrumb) */
  hideName?: boolean;
  /** Render the logo as a top-left overlay on the cover instead of overlapping its bottom edge */
  logoOverlay?: boolean;
  /** Ref attached to the logo wrapper, e.g. to observe when it scrolls out of view */
  logoRef?: React.Ref<HTMLDivElement>;
  /** Full-bleed cover: drop the top rounding and keep the name/bio text constrained while the image spans its (full-width) container */
  fullBleed?: boolean;
  /** Cycle the cover through the gallery photos (auto-advancing, with arrows/dots/swipe) instead of showing a still cover */
  coverSlideshow?: boolean;
  /** Optional content rendered as an overlay in the top-right corner of the cover (e.g. website link) */
  coverTopRight?: React.ReactNode;
  /** Optional content rendered as an overlay in the bottom-left corner of the cover (e.g. opening status) */
  coverBottomLeft?: React.ReactNode;
  /** Optional content rendered as an overlay in the bottom-right corner of the cover, next to the photo count (e.g. follow/notify actions) */
  coverBottomRight?: React.ReactNode;
  onNameUpdated?: (name: string) => void;
  onBioUpdated?: (bio: string | undefined) => void;
  onCoverUpdated?: (url: string) => void;
  onLogoUpdated?: (url: string, smallUrl: string) => void;
  onGalleryUpdated?: (images: GalleryImage[]) => void;
}

export default function LocationHeader({
  locationId,
  name,
  bio,
  coverImageUrl,
  logoUrl,
  logoSmallUrl,
  galleryImages = [],
  fallbackIcon,
  isStaffMember,
  isStaffAdmin,
  hideName = false,
  logoOverlay = false,
  logoRef,
  fullBleed = false,
  coverSlideshow = false,
  coverTopRight,
  coverBottomLeft,
  coverBottomRight,
  onNameUpdated,
  onBioUpdated,
  onCoverUpdated,
  onLogoUpdated,
  onGalleryUpdated,
}: LocationHeaderProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  // Carousel state
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselStartIndex, setCarouselStartIndex] = useState(0);
  const [localGallery, setLocalGallery] = useState<GalleryImage[]>(galleryImages);
  const [localCoverUrl, setLocalCoverUrl] = useState(coverImageUrl);
  // Which slide the cover slideshow is showing, so a tap opens the modal on that photo
  const [activeSlide, setActiveSlide] = useState(0);

  // Logo upload state
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [localLogoUrl, setLocalLogoUrl] = useState(logoUrl);
  const [localLogoSmallUrl, setLocalLogoSmallUrl] = useState(logoSmallUrl);

  // Inline name editing
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(name);
  const [savingName, setSavingName] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Inline bio editing
  const [editingBio, setEditingBio] = useState(false);
  const [bioValue, setBioValue] = useState(bio ?? '');
  const [savingBio, setSavingBio] = useState(false);
  const bioInputRef = useRef<HTMLTextAreaElement>(null);

  const getApiOrigin = useCallback(() => {
    const baseURL = apiClient.getBaseURL();
    if (!baseURL) {
      return { host: window.location.hostname, port: window.location.port || undefined, protocol: window.location.protocol.replace(':', '') };
    }
    try {
      const url = new URL(baseURL);
      return { host: url.hostname, port: url.port || undefined, protocol: url.protocol.replace(':', '') };
    } catch {
      return { host: window.location.hostname, port: window.location.port || undefined, protocol: window.location.protocol.replace(':', '') };
    }
  }, []);

  // Slides for the cover slideshow: the cover leads, followed by the rest of the
  // gallery. The cover is usually one of the gallery photos ("set as cover"), so it
  // is matched by URL to avoid showing it twice. Each slide remembers which gallery
  // photo it maps to, so opening the modal lands on the image the user was looking at.
  const coverSlides = useMemo(() => {
    const slides: { url: string; galleryIndex: number }[] = [];
    if (localCoverUrl) {
      slides.push({ url: localCoverUrl, galleryIndex: Math.max(0, localGallery.findIndex(img => img.url === localCoverUrl)) });
    }
    localGallery.forEach((img, i) => {
      if (img.url !== localCoverUrl) slides.push({ url: img.url, galleryIndex: i });
    });
    return slides;
  }, [localCoverUrl, localGallery]);

  const slideshowActive = coverSlideshow && coverSlides.length > 1;

  // Open carousel on cover image click, on the photo currently on screen
  const handleCoverClick = () => {
    if (localGallery.length > 0 || localCoverUrl) {
      setCarouselStartIndex(slideshowActive ? coverSlides[activeSlide]?.galleryIndex ?? 0 : 0);
      setCarouselOpen(true);
    }
  };

  const handleCoverSet = (url: string) => {
    setLocalCoverUrl(url);
    onCoverUpdated?.(url);
  };

  const handleImageAdded = (image: GalleryImage) => {
    const newGallery = [...localGallery, image];
    setLocalGallery(newGallery);
    onGalleryUpdated?.(newGallery);
  };

  const handleImageDeleted = (imageId: string) => {
    const newGallery = localGallery.filter(img => img.id !== imageId);
    setLocalGallery(newGallery);
    onGalleryUpdated?.(newGallery);
  };

  const handleSaveName = async () => {
    if (!nameValue.trim() || nameValue.trim() === name) {
      setEditingName(false);
      setNameValue(name);
      return;
    }
    setSavingName(true);
    try {
      const result = await locationImagesService.updateProfile(locationId, { name: nameValue.trim() });
      onNameUpdated?.(result.name);
      setNameValue(result.name);
      setEditingName(false);
      toast({ title: t('locationImages.profileUpdated') });
    } catch {
      toast({ title: t('errors.unexpectedError'), variant: 'destructive' });
    } finally {
      setSavingName(false);
    }
  };

  const handleSaveBio = async () => {
    const trimmed = bioValue.trim() || undefined;
    setSavingBio(true);
    try {
      const result = await locationImagesService.updateProfile(locationId, { bio: trimmed });
      onBioUpdated?.(result.bio);
      setBioValue(result.bio ?? '');
      setEditingBio(false);
      toast({ title: t('locationImages.profileUpdated') });
    } catch {
      toast({ title: t('errors.unexpectedError'), variant: 'destructive' });
    } finally {
      setSavingBio(false);
    }
  };

  const hasCoverOrGallery = !!localCoverUrl || localGallery.length > 0;

  return (
    <>
      {/* Cover image section */}
      <div className="relative w-full">
        {/* Cover image */}
        <div
          className={`relative w-full aspect-[16/6] bg-muted overflow-hidden ${fullBleed ? '' : 'rounded-t-lg'} ${hasCoverOrGallery ? 'cursor-pointer' : ''}`}
          onClick={hasCoverOrGallery ? handleCoverClick : undefined}
        >
          {slideshowActive ? (
            <CoverSlideshow
              images={coverSlides.map(slide => slide.url)}
              alt={name}
              onIndexChange={setActiveSlide}
            />
          ) : localCoverUrl ? (
            <img
              src={localCoverUrl}
              alt={name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-muted-foreground/10">
              <span className="text-muted-foreground/30 text-6xl font-bold select-none">
                {name.charAt(0).toUpperCase()}
              </span>
            </div>
          )}

          {/* Top-right overlay: optional content (e.g. website) + staff "manage photos" button */}
          {(coverTopRight || isStaffMember) && (
            <div className="absolute top-3 right-3 flex items-center gap-2" onClick={e => e.stopPropagation()}>
              {coverTopRight}
              {isStaffMember && (
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-black/40 border-white/20 text-white hover:bg-black/60 gap-1.5"
                  onClick={() => {
                    if (localGallery.length > 0) {
                      setCarouselStartIndex(localGallery.length - 1);
                    } else {
                      setCarouselStartIndex(0);
                    }
                    setCarouselOpen(true);
                  }}
                >
                  <ImagePlus className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('locationImages.managePhotos')}</span>
                </Button>
              )}
            </div>
          )}

          {/* Bottom-right overlay: photo count badge + optional actions (e.g. follow/notify) */}
          {((localGallery.length > 0 && !isStaffMember) || coverBottomRight) && (
            <div className="absolute bottom-3 right-3 z-10 flex items-center gap-2">
              {localGallery.length > 0 && !isStaffMember && (
                <div className="bg-black/50 text-white text-xs px-2 py-1 rounded-full">
                  {localGallery.length} {t('locationImages.photos')}
                </div>
              )}
              {coverBottomRight && (
                <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                  {coverBottomRight}
                </div>
              )}
            </div>
          )}

          {/* Bottom-left overlay (e.g. opening status) */}
          {coverBottomLeft && (
            <div className="absolute bottom-3 left-3 z-10" onClick={e => e.stopPropagation()}>
              {coverBottomLeft}
            </div>
          )}
        </div>

        {/* Logo - either overlaid on the top-left of the cover, or overlapping its bottom edge */}
        <div ref={logoRef} className={logoOverlay ? 'absolute top-3 left-3 z-10' : 'absolute -bottom-10 left-4'}>
          <div className="relative">
            <Avatar className="w-20 h-20 border-4 border-background shadow-md">
              <AvatarImage
                src={localLogoSmallUrl || localLogoUrl}
                alt={name}
                className="object-cover"
              />
              <AvatarFallback className="text-lg font-bold bg-muted">
                {fallbackIcon ?? <Mountain className="w-8 h-8 text-muted-foreground" />}
              </AvatarFallback>
            </Avatar>

            {/* Staff: edit logo button */}
            {isStaffMember && (
              <DirectUploadProvider
                origin={getApiOrigin()}
                headers={{ Authorization: `Bearer ${tokenStorage.getToken() || ''}` }}
                onSuccess={async (signedIds: string[]) => {
                  if (!signedIds[0]) return;
                  try {
                    const result = await locationImagesService.updateLogo(locationId, signedIds[0]);
                    setLocalLogoUrl(result.logo_url);
                    setLocalLogoSmallUrl(result.logo_small_url);
                    onLogoUpdated?.(result.logo_url, result.logo_small_url);
                    toast({ title: t('locationImages.logoUpdated') });
                  } catch {
                    toast({ title: t('errors.unexpectedError'), variant: 'destructive' });
                  } finally {
                    setUploadingLogo(false);
                  }
                }}
                render={({ handleUpload, ready }) => {
                  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                    if (e.target.files && e.target.files.length > 0) {
                      setUploadingLogo(true);
                      handleUpload(Array.from(e.target.files));
                    }
                    e.target.value = '';
                  };
                  return (
                    <>
                      <input type="file" accept="image/*" id="logo-gallery-input" className="hidden" onChange={handleChange} disabled={!ready || uploadingLogo} />
                      <input type="file" accept="image/*" capture="environment" id="logo-camera-input" className="hidden" onChange={handleChange} disabled={!ready || uploadingLogo} />
                      <label htmlFor="logo-gallery-input" className="absolute -bottom-1 -right-1">
                        <div className={`w-7 h-7 rounded-full bg-background border border-border shadow flex items-center justify-center cursor-pointer hover:bg-muted transition-colors ${uploadingLogo ? 'pointer-events-none' : ''}`}>
                          {uploadingLogo
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                            : <Camera className="h-3.5 w-3.5 text-muted-foreground" />
                          }
                        </div>
                      </label>
                    </>
                  );
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Name + Bio section (below cover, with offset for the overlapping logo) */}
      {(!hideName || bio || bioValue || isStaffMember) && (
      <div className={`${logoOverlay ? 'pt-3' : 'pt-12'} ${fullBleed ? 'max-w-4xl mx-auto px-4 sm:px-6' : 'px-1'} space-y-2`}>
        {/* Name */}
        {!hideName && (
        <div className="flex items-center gap-2">
          {editingName ? (
            <div className="flex items-center gap-2 flex-1">
              <input
                ref={nameInputRef}
                autoFocus
                value={nameValue}
                onChange={e => setNameValue(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleSaveName();
                  if (e.key === 'Escape') { setEditingName(false); setNameValue(name); }
                }}
                className="text-2xl font-bold bg-transparent border-b-2 border-primary outline-none flex-1 min-w-0"
              />
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={handleSaveName} disabled={savingName}>
                {savingName ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4 text-green-600" />}
              </Button>
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEditingName(false); setNameValue(name); }}>
                <X className="h-4 w-4 text-muted-foreground" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">{name}</h1>
              {isStaffAdmin && (
                <Button size="icon" variant="ghost" className="h-7 w-7 opacity-50 hover:opacity-100" onClick={() => { setEditingName(true); setTimeout(() => nameInputRef.current?.focus(), 0); }}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          )}
        </div>
        )}

        {/* Bio */}
        {editingBio ? (
          <div className="space-y-2">
            <textarea
              ref={bioInputRef}
              autoFocus
              value={bioValue}
              onChange={e => setBioValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Escape') { setEditingBio(false); setBioValue(bio ?? ''); }
              }}
              rows={3}
              maxLength={500}
              placeholder={t('locationImages.bioPlaceholder')}
              className="w-full text-sm text-muted-foreground bg-transparent border border-border rounded-md p-2 outline-none focus:border-primary resize-none"
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSaveBio} disabled={savingBio}>
                {savingBio && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
                {t('common.save')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setEditingBio(false); setBioValue(bio ?? ''); }}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-2 group/bio">
            {(bio || bioValue) ? (
              <p className="text-sm text-muted-foreground flex-1">{bio || bioValue}</p>
            ) : isStaffMember ? (
              <p className="text-sm text-muted-foreground/50 italic flex-1">{t('locationImages.addBio')}</p>
            ) : null}
            {isStaffMember && (
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 opacity-0 group-hover/bio:opacity-50 hover:!opacity-100 shrink-0 mt-0.5"
                onClick={() => { setEditingBio(true); setTimeout(() => bioInputRef.current?.focus(), 0); }}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        )}
      </div>
      )}

      {/* Carousel modal */}
      {carouselOpen && (
        <LocationImageCarousel
          images={localGallery}
          initialIndex={carouselStartIndex}
          locationId={locationId}
          isStaffMember={isStaffMember}
          onClose={() => setCarouselOpen(false)}
          onCoverSet={(url) => handleCoverSet(url)}
          onImageAdded={handleImageAdded}
          onImageDeleted={handleImageDeleted}
        />
      )}
    </>
  );
}
